import { PropsWithChildren, useEffect } from 'react';
import { ClinicalBarData, ClinicalDataBars } from '@features/components/ClinicalBarChart';
import { ClinicalSection } from './ClinicalSection';
import { OrganType } from '@/api/apis/case';
import { ClinicalNavigation } from './ClinicalNavigation';
import { useRowScroll } from '@/hooks/useRowScroll';

type BarChartWidgetProps = {
  color: string;
  icon: string;
  header: string;
  organType: OrganType;
  values: ClinicalBarData[];
  baseLine?: number;
} & PropsWithChildren;

export const BarChartWidget = ({ color, icon, header, children, organType, values, baseLine }: BarChartWidgetProps) => {
  const {
    listContainerRef,
    listItemRefs,
    onNavigationPrev,
    onNavigationNext,
    maxLeft,
    maxRight,
    hasScrollableContent,
  } = useRowScroll();

  useEffect(() => {
    if (listContainerRef?.current) {
      listContainerRef.current.scrollBy({ left: listContainerRef.current.scrollWidth });
    }
  }, [values, listContainerRef]);

  return (
    <ClinicalSection
      color={color}
      header={header}
      icon={icon}
      endContent={
        hasScrollableContent ? (
          <ClinicalNavigation
            label={`${values.length} Readings`}
            prev={onNavigationPrev}
            next={onNavigationNext}
            prevDisabled={maxLeft}
            nextDisabled={maxRight}
          />
        ) : (
          values.length > 0 && <span className="text-s-r text-base-20">{values.length} Readings</span>
        )
      }
    >
      {values.length > 0 && (
        <div className={`flex flex-col gap-3 w-full scroll ${organType}`}>
          {children}
          <div ref={listContainerRef} className="w-full flex gap-2 overflow-x-auto pb-2">
            <ClinicalDataBars
              listItemRefs={listItemRefs}
              organType={organType}
              values={values}
              baseline={baseLine}
              maxVerticalBarHeight={65}
              verticalView
              showLabel
              showValue
            />
          </div>
        </div>
      )}
    </ClinicalSection>
  );
};
